<script setup lang="ts">
    import { computed, shallowReactive } from 'vue';
    import { useRoute } from 'vue-router';
    import SelectorDialog from '@Quest/PortfolioScenarios/Components/SelectorDialog.vue';
    import PortfolioScenarioEditor from "@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor.vue";
    import { SelectorDialogViewModel } from "@Quest/PortfolioScenarios/Components/SelectorDialog";
    import { PortfolioScenarioEditorViewModel } from "@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor";

    // props
    const props = defineProps<{
        portfolioId: string
        corporateScenarioUrl: string
    }>();

    // emits
    const emit = defineEmits<{
        (e: 'selector-opened'): void
        (e: 'settings-opened'): void
        (e: 'selected', result: any): void
        (e: 'scenario-created', id: string): void
        (e: 'scenario-updated', id: string): void
    }>();

    const route = useRoute();
    const currentPath = computed(() => route.path)

    // route-derived values
    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0));
    const tchProjectId = computed(() => Number(route.params?.tchProjectId ?? 0));
    const scenarioId = computed(() => String(route.params?.scenarioId ?? ''));

    const selectorDialog = shallowReactive(new SelectorDialogViewModel());
    const portfolioScenarioEditor = shallowReactive(new PortfolioScenarioEditorViewModel());

    function getButtonClass(path: string): string
    {
        const routePrefix = `/portfolio-plan/${path}/`;

        return currentPath.value.startsWith(routePrefix)
            ? 'btn btn-secondary'
            : 'btn btn-outline-secondary';
    }

    function showSelectorDialog()
    {
        emit('selector-opened');

        selectorDialog.show(
            workspaceId.value,
            props.portfolioId,
            scenarioId.value
        );
    }

    function showScenarioEditor()
    {
        emit('settings-opened');

        portfolioScenarioEditor.showEditor(
            workspaceId.value,
            props.portfolioId,
            scenarioId.value
        );
    }

    function onSelected(result: any)
    {
        emit('selected', result)
    }

    function onScenarioCreated(id: string)
    {
        emit('scenario-created', id)
    }

    function onScenarioUpdated(id: string)
    {
        emit('scenario-updated', id)
    }

</script>


<template>
    <div>
        <PortfolioScenarioEditor :viewModel="portfolioScenarioEditor"
                                 @scenario-created="onScenarioCreated"
                                 @scenario-updated="onScenarioUpdated" />

        <SelectorDialog :viewModel="selectorDialog" @selected="onSelected" />

        <div class="text-right align-self-end">
            <button type="button" title="Open Selector (AI Assistant)"
                    class="btn btn-success mr-1"
                    style="padding: 0.7em 1em;"
                    @click="showSelectorDialog">

                <i class="fa-solid fa-robot fa-lg mr-1"></i> Selector
            </button>

            <div class="btn-group mr-1" role="group">
                <a title="Project Settings" :class="getButtonClass('projects')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio-plan/projects/${workspaceId}/${tchProjectId}/${scenarioId}`">
                    <i class="fa-solid fa-table fa-lg"></i>
                </a>
                <a title="Project Selection" :class="getButtonClass('selection')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio-plan/selection/${workspaceId}/${tchProjectId}/${scenarioId}`">
                    <i class="fa-solid fa-list-check fa-lg"></i>
                </a>
                <a title="Project Schedule" :class="getButtonClass('schedule')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio-plan/schedule/${workspaceId}/${tchProjectId}/${scenarioId}`">
                    <i class="fa-regular fa-calendar fa-lg"></i>
                </a>
                <a title="Project Review" :class="getButtonClass('review')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio-plan/review/${workspaceId}/${tchProjectId}/${scenarioId}`">
                    <i class="fa-solid fa-database fa-lg"></i>
                </a>
                <a title="Project Approval" :class="getButtonClass('approval')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio-plan/approval/${workspaceId}/${tchProjectId}/${scenarioId}`">
                    <i class="fa-solid fa-pen-nib fa-lg"></i>
                </a>
            </div>


            <a title="Profit & Loss Report" class="mr-1" :class="getButtonClass('pnl')" style="padding: 0.7em 2em;" v-sanitizedHref="`/#/portfolio-plan/pnl/${workspaceId}/${tchProjectId}/${scenarioId}`">
                P&L
            </a>

            <button title="Portfolio Plan Settings" class="btn btn-outline-secondary mr-1" style="padding: 0.7em 1em;" @click="showScenarioEditor">
                <i class="fa fa-lg fa-cog"></i>
            </button>

            <div class="btn-group mr-1" role="group">
                <a title="Enterprise Plan" class="btn btn-outline-secondary" style="padding: 0.7em 1em;" v-sanitizedHref="corporateScenarioUrl">
                    <i class="fa-solid fa-sitemap fa-lg"></i>
                </a>
                <a title="Back to Portfolio Plans" class="btn btn-outline-secondary" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/portfolio/plans/${workspaceId}/${tchProjectId}`">
                    <i class="fa-solid fa-door-open fa-lg"></i>
                </a>
            </div>
        </div>
    </div>
</template>